import i18next from 'i18next';
import XLSX from 'xlsx';
import { format } from 'date-fns';
import { capitalizeFirstLetters } from 'utilities/string-utils';
import { ReportParams, ReportProduct, ReportWorkbook } from '../types';
import { resolveDateRange } from './report-date-utils';
import { PRODUCT_FAMILY_OPTIONS_MAP } from './report-param-utils';

const DATE_FORMAT = 'yyyyMMdd';

const REPORT_PREFIX = {
  [ReportProduct.KEY]: 'KWT',
  [ReportProduct.WDT]: 'WDT',
  [ReportProduct.PRM]: 'PRM'
};
const getReportDetailsWorksheet = (
  name: string | undefined,
  reportParams: ReportParams
): any[][] => {
  const { productFamilyCode, dateRange, entityType } = reportParams;
  const { dateStart, dateEnd } = resolveDateRange(dateRange);

  return [
    [i18next.t('Entity Name'), name],
    [i18next.t('Entity Type'), i18next.t(entityType, { ns: 'Reports' })],
    [
      i18next.t('Product'),
      i18next.t(PRODUCT_FAMILY_OPTIONS_MAP[productFamilyCode]?.value, { ns: 'ProductFamily' })
    ],
    [
      i18next.t('Report Date Range'),
      `From ${dateStart.toLocaleDateString('en-US')} to ${dateEnd.toLocaleDateString('en-US')}`
    ],
    [i18next.t('Report Generation Date'), new Date()]
  ];
};

const mapDataToHeaderAndRows = (data: any[]): any[][] => {
  const header =
    data.length > 0
      ? Object.keys(data[0]).map(key => i18next.t(key, { ns: 'ReportDownload' }))
      : [];
  const rows = data.map((x: any) => Object.values(x));
  return [header, ...rows];
};

export const exportReport = <T>(
  workbook: ReportWorkbook<T>,
  data: T,
  reportParams: ReportParams
): void => {
  const { dateStart, dateEnd } = resolveDateRange(reportParams.dateRange);
  const entityName = workbook.getEntityName(data);
  const exportFileName = `${
    REPORT_PREFIX[reportParams.productFamilyCode]
  }-${entityName}-Report-${format(dateStart, DATE_FORMAT)}-${format(dateEnd, DATE_FORMAT)}.xlsx`;

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.aoa_to_sheet(getReportDetailsWorksheet(entityName, reportParams)),
    i18next.t('Report Details', { ns: 'Reports' })
  );

  workbook.worksheets.forEach(({ name, parseQueryData }) =>
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet(mapDataToHeaderAndRows(parseQueryData(data, reportParams))),
      capitalizeFirstLetters(i18next.t(name, { ns: 'Reports' }))
    )
  );
  XLSX.writeFile(wb, exportFileName);
};
